import { useEffect, useRef } from "react";
import { Trash2 } from "lucide-react";
import { isVocalCategory } from "../lib/audioEngine";
import { TxSelect } from "./TxSelect";

// Vertical mixing console (Pro-Tools style): channel strips + master strip,
// with live VU-meters, faders, pan, inserts and solo/mute.

const VFader = ({ value, min, max, step, onChange, testid, accent = "#FF5500" }) => (
  <input
    type="range" min={min} max={max} step={step} value={value}
    onChange={(e) => onChange(parseFloat(e.target.value))}
    data-testid={testid}
    className="vfader"
    style={{ WebkitAppearance: "slider-vertical", appearance: "slider-vertical", width: "22px", height: "150px", accentColor: accent }}
  />
);

const MiniKnob = ({ label, value, min, max, step, onChange, testid }) => (
  <label className="flex flex-col items-center gap-0.5 w-full" title={`${label}: ${value}`}>
    <span className="text-[9px] font-mono text-[#64748B] uppercase leading-none">{label}</span>
    <input type="range" min={min} max={max} step={step} value={value}
      onChange={(e) => onChange(parseFloat(e.target.value))} data-testid={testid}
      className="w-full h-1 accent-[#06B6D4]" />
  </label>
);

const Chip = ({ label, on, onClick, testid, color = "#06B6D4" }) => (
  <button onClick={onClick} data-testid={testid}
    className="text-[9px] font-mono font-bold rounded px-1 py-0.5 transition-colors"
    style={{ background: on ? color : "#1A1F2C", color: on ? "#0B0D12" : "#64748B" }}>
    {label}
  </button>
);

function Meter({ nodeRef }) {
  return (
    <div className="relative w-2.5 h-[150px] rounded bg-[#0B0D12] overflow-hidden border border-[#262C3A]">
      <div ref={nodeRef} className="absolute bottom-0 left-0 right-0"
        style={{ height: "0%", background: "linear-gradient(to top,#10B981 0%,#10B981 60%,#F59E0B 80%,#EF4444 100%)", transition: "height 60ms linear" }} />
    </div>
  );
}

export function MixerConsole({ tracks, anySolo, updateTrack, toggleFx, removeTrack, setRole, playRef, playing, processedLabel, catLabel, voiceBadge, t, catColors }) {
  const metersRef = useRef({});     // trackId -> DOM node
  const masterRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    const buf = new Uint8Array(256);
    const rms = (analyser) => {
      analyser.getByteTimeDomainData(buf);
      let s = 0;
      for (let i = 0; i < buf.length; i++) { const v = (buf[i] - 128) / 128; s += v * v; }
      return Math.sqrt(s / buf.length);
    };
    const toPct = (r) => Math.min(100, Math.pow(r, 0.6) * 170); // perceptual-ish scaling
    const loop = () => {
      const pr = playRef.current;
      if (pr) {
        for (const src of pr.sources) {
          const node = metersRef.current[src.trackId];
          if (node && src.meter) node.style.height = toPct(rms(src.meter)) + "%";
        }
        if (masterRef.current && pr.analyser) masterRef.current.style.height = toPct(rms(pr.analyser)) + "%";
      }
      rafRef.current = requestAnimationFrame(loop);
    };
    if (playing) { rafRef.current = requestAnimationFrame(loop); }
    const meters = metersRef.current;
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      Object.values(meters).forEach((n) => { if (n) n.style.height = "0%"; });
      if (masterRef.current) masterRef.current.style.height = "0%";
    };
  }, [playing, playRef]);

  if (!tracks.length) {
    return <div className="text-center text-[#64748B] py-16" data-testid="console-empty">{t("studio.noTracks")}</div>;
  }

  return (
    <div className="bg-[#0d1017] border border-[#262C3A] rounded-2xl p-3 overflow-x-auto tx-dark-scroll" data-testid="mixer-console">
      <div className="flex gap-2 items-stretch min-w-min">
        {tracks.map((tr) => {
          const col = catColors[tr.category] || "#64748B";
          const dim = anySolo && !tr.solo;
          const hasEq = tr.eqLow || tr.eqMid || tr.eqHigh;
          return (
            <div key={tr.id} data-testid={`track-${tr.id}`}
              className={`shrink-0 w-[108px] flex flex-col items-center gap-2 rounded-xl bg-[#131722] border p-2 transition-opacity ${dim ? "opacity-50" : ""}`}
              style={{ borderColor: col + "44" }}>
              {/* name + category */}
              <div className="w-full text-center">
                <div className="flex items-center justify-center gap-1">
                  <span className="h-2 w-2 rounded-full shrink-0" style={{ background: col }} />
                  <span className="text-[11px] font-semibold truncate max-w-[82px]" title={tr.name}>{tr.name}</span>
                </div>
                <span className="font-mono text-[8px] uppercase px-1.5 py-0.5 rounded-full inline-block mt-1" data-testid={`track-category-${tr.id}`} style={{ background: col + "22", color: col }}>{catLabel(tr.category)}</span>
                {isVocalCategory(tr.category) && <span className="font-mono text-[8px] uppercase px-1 py-0.5 rounded-full bg-[#FF5500]/20 text-[#FF5500] ml-1" data-testid={`track-voice-${tr.id}`}>● {voiceBadge}</span>}
              </div>

              {/* manual role override — tell the AI what this track is */}
              <TxSelect
                value={tr.category} onValueChange={(v) => setRole(tr.id, v)} testid={`role-${tr.id}`}
                triggerClassName="h-8 px-1.5 text-[10px] rounded-md text-[#94A3B8] w-full"
                items={["voice_lead", "voice_choir", "drum", "bass", "synth", "lead", "guitar", "other"].map((c) => ({ value: c, label: catLabel(c) }))}
              />

              {/* EQ (top of strip) */}
              <div className="w-full space-y-1 border-y border-[#262C3A] py-1.5">
                <MiniKnob label="Hi" value={tr.eqHigh} min={-12} max={12} step={1} onChange={(v) => updateTrack(tr.id, { eqHigh: v })} testid={`eqhi-${tr.id}`} />
                <MiniKnob label="Mid" value={tr.eqMid} min={-12} max={12} step={1} onChange={(v) => updateTrack(tr.id, { eqMid: v })} testid={`eqmid-${tr.id}`} />
                <MiniKnob label="Low" value={tr.eqLow} min={-12} max={12} step={1} onChange={(v) => updateTrack(tr.id, { eqLow: v })} testid={`eqlow-${tr.id}`} />
              </div>

              {/* inserts */}
              <div className="flex flex-wrap justify-center gap-1 w-full">
                <Chip label="HPF" on={tr.hpf} onClick={() => updateTrack(tr.id, { hpf: !tr.hpf })} testid={`hpf-${tr.id}`} />
                <Chip label="DE" on={tr.deEsser} onClick={() => toggleFx(tr.id, "deEsser")} testid={`deesser-${tr.id}`} color="#8B5CF6" />
                <Chip label="GT" on={tr.gate} onClick={() => toggleFx(tr.id, "gate")} testid={`gate-${tr.id}`} color="#10B981" />
                <Chip label="AT" on={tr.autoTune} onClick={() => toggleFx(tr.id, "autoTune")} testid={`autotune-${tr.id}`} color="#EC4899" />
                <span className="text-[9px] font-mono font-bold rounded px-1 py-0.5 bg-[#262C3A] text-[#94A3B8]" title="Compresseur par piste (auto)">CP</span>
              </div>

              {/* pan */}
              <MiniKnob label="Pan" value={tr.pan} min={-1} max={1} step={0.1} onChange={(v) => updateTrack(tr.id, { pan: v })} testid={`pan-${tr.id}`} />

              {/* fader + meter */}
              <div className="flex items-end gap-1.5 py-1">
                <VFader value={tr.gain} min={-24} max={12} step={1} onChange={(v) => updateTrack(tr.id, { gain: v })} testid={`fader-${tr.id}`} accent={col} />
                <Meter nodeRef={(el) => { metersRef.current[tr.id] = el; }} />
              </div>
              <div className="font-mono text-[10px] text-[#94A3B8]">{tr.gain > 0 ? "+" : ""}{tr.gain} dB</div>

              {/* solo / mute */}
              <div className="flex items-center gap-1.5 w-full justify-center">
                <button onClick={() => updateTrack(tr.id, { solo: !tr.solo })} data-testid={`solo-${tr.id}`}
                  className={`w-7 h-7 rounded-md text-[11px] font-bold transition-colors ${tr.solo ? "bg-[#F59E0B] text-black" : "bg-[#1A1F2C] hover:bg-[#262C3A] text-[#94A3B8]"}`}>S</button>
                <button onClick={() => updateTrack(tr.id, { mute: !tr.mute })} data-testid={`mute-${tr.id}`}
                  className={`w-7 h-7 rounded-md text-[11px] font-bold transition-colors ${tr.mute ? "bg-[#EF4444] text-white" : "bg-[#1A1F2C] hover:bg-[#262C3A] text-[#94A3B8]"}`}>M</button>
                <button onClick={() => removeTrack(tr.id)} data-testid={`delete-${tr.id}`}
                  className="w-7 h-7 rounded-md bg-[#1A1F2C] hover:bg-[#EF4444] flex items-center justify-center text-[#94A3B8] hover:text-white transition-colors"><Trash2 className="h-3 w-3" /></button>
              </div>
              {tr.playBuffer && <span className="text-[9px] font-mono text-[#10B981]">● {processedLabel}</span>}
            </div>
          );
        })}

        {/* MASTER strip */}
        <div className="shrink-0 w-[108px] flex flex-col items-center gap-2 rounded-xl bg-[#1a1120] border border-[#FF5500]/40 p-2" data-testid="master-strip">
          <div className="text-[11px] font-heading font-bold text-[#FF5500] text-center">MASTER</div>
          <div className="h-[86px] flex items-center text-[9px] font-mono text-[#64748B]">·</div>
          <div className="flex items-end gap-1.5 py-1">
            <div className="w-[22px] h-[150px] flex items-center justify-center text-[9px] font-mono text-[#64748B]">BUS</div>
            <Meter nodeRef={(el) => { masterRef.current = el; }} />
          </div>
          <div className="font-mono text-[10px] text-[#94A3B8]">0 dB</div>
          <div className="text-[9px] font-mono text-[#64748B] text-center leading-tight">{t("studio.masterTitle")}</div>
        </div>
      </div>
    </div>
  );
}
